import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { InfoHint } from "@dolfin/ui";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";

type Registration = components["schemas"]["EInvoicingRegistrationAdminResponse"];
type RegistrationState = NonNullable<components["schemas"]["EInvoicingRegistrationState"]>;
type FaultDetail = components["schemas"]["ProviderFaultDetail"];

interface EInvoicingRegistrationsTableProps {
  /** Scope to one client. Also drops the client column — on a client page it would repeat the page. */
  clientId?: string;
  /** Bumping this triggers a refetch. */
  refreshKey?: number;
}

/** Nothing to show. A single hyphen, per the house rule on user-visible text. */
const EMPTY = "-";

/** Radix Select can't hold "", so "no filter" is a sentinel mapped back to undefined. */
const ANY = "any";

/** Not EInvoicingRegistrationStatus: a registration row only exists once submitted, so there's no NotStarted. */
const STATES: { id: RegistrationState; label: string }[] = [
  { id: "Submitted", label: "Submitted" },
  { id: "Processing", label: "Processing" },
  { id: "ActionRequired", label: "Action required" },
  { id: "Registered", label: "Registered" },
  { id: "Failed", label: "Failed" },
];

function stateVariant(state: string): "default" | "secondary" | "outline" | "destructive" {
  switch (state) {
    case "Registered":
      return "default";
    case "Failed":
      return "destructive";
    case "ActionRequired":
      return "secondary";
    default:
      return "outline";
  }
}

/** Time in flight. A Submitted row unmoved for a week usually means a callback never arrived. */
function daysWaiting(registration: Registration): number | null {
  if (registration.state === "Registered" || registration.state === "Failed") return null;
  const since = registration.submittedAt ?? registration.createdAt;
  if (!since) return null;
  return Math.floor((Date.now() - new Date(since).getTime()) / 86_400_000);
}

/** Submission count, flagged above one: a retry always means someone hit a problem. */
function AttemptCount({ count }: { count: string | number | null | undefined }) {
  if (count === null || count === undefined) return <>{EMPTY}</>;
  const value = typeof count === "string" ? Number(count) : count;
  if (!Number.isFinite(value)) return <>{EMPTY}</>;
  if (value > 1) {
    return <span className="font-medium text-amber-600 dark:text-amber-400">{value}</span>;
  }
  return <>{value}</>;
}

/** The at-a-glance answer: a tick means the provider had nothing to complain about. */
function FaultIndicator({ registration }: { registration: Registration }) {
  const count = registration.faults?.length ?? 0;

  if (count === 0) {
    return (
      <span className="inline-flex items-center text-muted-foreground/40">
        <Check className="size-4" aria-hidden />
        <span className="sr-only">No faults</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
      <AlertTriangle className="size-4 shrink-0" aria-hidden />
      <span className="text-xs font-medium tabular-nums">{count}</span>
      <span className="sr-only">{`${count} ${count === 1 ? "fault" : "faults"}`}</span>
    </span>
  );
}

export function EInvoicingRegistrationsTable({
  clientId,
  refreshKey = 0,
}: EInvoicingRegistrationsTableProps) {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Registration | null>(null);
  const [state, setState] = useState<RegistrationState | typeof ANY>(ANY);

  const showClient = !clientId;

  const stateQuery = state === ANY ? undefined : state;

  const pagination = useCursorPagination({ initialLimit: 50 });

  // Reset the cursor during render and in state, not an effect or a ref: either would let the stale cursor
  // go out against the new filter.
  const filterKey = [clientId ?? "", stateQuery ?? ""].join("|");
  const [cursorFilterKey, setCursorFilterKey] = useState(filterKey);
  if (cursorFilterKey !== filterKey) {
    setCursorFilterKey(filterKey);
    pagination.reset();
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchRegistrations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/e-invoicing/registrations", {
        params: {
          query: {
            clientId,
            state: stateQuery,
            cursor: pagination.cursor,
            limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load e-invoicing registrations.");
      } else {
        setRegistrations(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchRegistrations();
    return () => {
      cancelled = true;
    };
  }, [clientId, stateQuery, refreshKey, pagination.cursor, pagination.limit]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={state} onValueChange={(v) => setState(v as RegistrationState | typeof ANY)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Any state" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any state</SelectItem>
            {STATES.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Spinner className="size-5" />}
      </div>

      {error ? (
        <p className="text-destructive">{error}</p>
      ) : registrations.length === 0 && !loading ? (
        <div>
          <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
            No registrations here. With no state filter that means nobody has submitted one yet.
          </p>
          {/* Still shown: paging forward onto an empty page has to be escapable. */}
          <PaginationBar
            hasPrevious={pagination.hasPrevious}
            hasNext={pagination.hasMore}
            isLoading={loading}
            limit={pagination.limit}
            limitOptions={[20, 50, 100]}
            onPrevious={pagination.goPrevious}
            onNext={pagination.goNext}
            onLimitChange={pagination.setLimit}
          />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>
                  <span className="inline-flex items-center gap-1">
                    Faults
                    <InfoHint label="Faults">
                      What the provider said went wrong. Open the row to read them, including which
                      workflow step failed and when.
                    </InfoHint>
                  </span>
                </TableHead>
                <TableHead>Organisation</TableHead>
                {showClient && <TableHead>Client</TableHead>}
                <TableHead>Country</TableHead>
                <TableHead>Tax number</TableHead>
                <TableHead>Participant</TableHead>
                <TableHead className="text-right">Attempts</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">
                  <span className="inline-flex items-center gap-1">
                    Waiting
                    <InfoHint label="Waiting">
                      Days since submission, for registrations still in flight. A Submitted row that
                      has not moved in days is the clearest sign a callback never arrived.
                    </InfoHint>
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registrations.map((r) => {
                const waiting = daysWaiting(r);
                return (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)}>
                    <TableCell>
                      <Badge variant={stateVariant(r.state ?? "")}>{r.state}</Badge>
                    </TableCell>
                    <TableCell>
                      <FaultIndicator registration={r} />
                    </TableCell>
                    <TableCell className="max-w-[16rem] truncate">
                      {r.organisationId ? (
                        // Stop the row's own click: this is a link out, not a row selection.
                        <Link
                          to={`/organisations/${r.organisationId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary hover:underline"
                        >
                          {r.organisationName ?? r.organisationId.slice(0, 8)}
                        </Link>
                      ) : (
                        (r.organisationName ?? EMPTY)
                      )}
                    </TableCell>
                    {showClient && (
                      <TableCell className="font-mono text-xs">
                        {r.clientId ? (
                          // Stop the row's own click: this is a link out, not a row selection.
                          <Link
                            to={`/clients/${r.clientId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary hover:underline"
                          >
                            {r.clientId.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">deleted</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>{r.taxCountry ?? EMPTY}</TableCell>
                    <TableCell className="font-mono text-xs">{r.taxNumber ?? EMPTY}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.electronicAddress
                        ? `${r.electronicAddressScheme ?? ""}:${r.electronicAddress}`
                        : EMPTY}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {/* Coerced: integers arrive as strings on some routes and numbers on others. */}
                      <AttemptCount count={r.submissionCount} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.submittedAt ? formatDateTime(r.submittedAt) : EMPTY}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {waiting === null ? (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      ) : waiting >= 2 ? (
                        <span className="font-medium text-amber-600 dark:text-amber-400">
                          {waiting}d
                        </span>
                      ) : (
                        `${waiting}d`
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <PaginationBar
            hasPrevious={pagination.hasPrevious}
            hasNext={pagination.hasMore}
            isLoading={loading}
            limit={pagination.limit}
            limitOptions={[20, 50, 100]}
            onPrevious={pagination.goPrevious}
            onNext={pagination.goNext}
            onLimitChange={pagination.setLimit}
          />
        </>
      )}

      <RegistrationSheet registration={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

/** Everything the route returns; the step trail shows which step produced a failure, and when. */
function RegistrationSheet({
  registration,
  onClose,
}: {
  registration: Registration | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={!!registration} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        className="w-full overflow-y-auto sm:max-w-xl"
        // Radix focuses the first focusable child on open, which here is a copy button - and that
        // pops its tooltip open over a field label before the reader has done anything.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {registration && (
          <div className="space-y-6">
            <SheetHeader className="space-y-1">
              <SheetTitle>{registration.organisationName ?? "Registration"}</SheetTitle>
              <SheetDescription>
                {registration.legalName ?? "No legal name recorded"}
              </SheetDescription>
            </SheetHeader>

            <Section title="Status">
              <Field label="State">
                <Badge variant={stateVariant(registration.state ?? "")}>{registration.state}</Badge>
              </Field>
              <Field label="Network">{registration.network ?? EMPTY}</Field>
              <Field label="Provider state" mono>
                {/* The provider's own state for the party document, which is not our state. */}
                {registration.providerState ?? EMPTY}
              </Field>
              <Field label="Attempts">{registration.submissionCount ?? EMPTY}</Field>
            </Section>

            <Section title="Party">
              <Field label="Tax country">{registration.taxCountry ?? EMPTY}</Field>
              <Field label="Tax number" mono>
                {registration.taxNumber ?? EMPTY}
              </Field>
              <Field label="Participant" mono full>
                {registration.electronicAddress ? (
                  <Copyable value={registration.electronicAddress}>
                    {`${registration.electronicAddressScheme ?? ""}:${registration.electronicAddress}`}
                  </Copyable>
                ) : (
                  <span className="font-sans text-muted-foreground">
                    not assigned - the network assigns this on success
                  </span>
                )}
              </Field>
            </Section>

            <Section title="Faults">
              <Field label="What went wrong" full>
                <FaultDetailList details={registration.faults} />
              </Field>
            </Section>

            <Section title="Proof of ownership">
              <Field label="Document" full>
                {registration.ownershipDocumentName ? (
                  <>
                    {registration.ownershipDocumentName}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {formatDateTime(registration.ownershipDocumentUploadedAt)}
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    none uploaded
                    {registration.state === "ActionRequired" && " - this is what it is waiting for"}
                  </span>
                )}
              </Field>
            </Section>

            <Section title="Timeline">
              <Field label="Created">{formatDateTime(registration.createdAt)}</Field>
              <Field label="Submitted">
                {registration.submittedAt ? formatDateTime(registration.submittedAt) : EMPTY}
              </Field>
              <Field label="Registered">
                {registration.registeredAt ? formatDateTime(registration.registeredAt) : EMPTY}
              </Field>
              <Field label="Failed">
                {registration.failedAt ? formatDateTime(registration.failedAt) : EMPTY}
              </Field>
              <Field label="Last callback" full>
                {registration.lastCallbackAt ? (
                  formatDateTime(registration.lastCallbackAt)
                ) : (
                  // The distinction worth drawing: never heard from is a different problem from refused.
                  <span className="text-muted-foreground">
                    never - the provider has not called back about this at all
                  </span>
                )}
              </Field>
            </Section>

            <Section title="Identifiers">
              <Field label="Registration" mono>
                <Copyable value={registration.id}>{registration.id ?? EMPTY}</Copyable>
              </Field>
              <Field label="Provider job" mono>
                {/* The run to open in the provider's console when this sheet is not enough. */}
                <Copyable value={registration.providerJobId}>
                  {registration.providerJobId ?? EMPTY}
                </Copyable>
              </Field>
              <Field label="Organisation" mono full>
                {registration.organisationId ? (
                  <span className="flex items-start gap-2">
                    <Link
                      to={`/organisations/${registration.organisationId}`}
                      className="break-all text-primary hover:underline"
                    >
                      {registration.organisationId}
                    </Link>
                    <CopyButton value={registration.organisationId} className="mt-0.5 shrink-0" />
                  </span>
                ) : (
                  <span className="font-sans text-muted-foreground">none</span>
                )}
              </Field>
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 border-b pb-1 text-xs font-semibold uppercase tracking-wide text-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-x-4 gap-y-4">{children}</dl>
    </div>
  );
}

function Field({
  label,
  children,
  mono,
  full,
}: {
  label: string;
  children: React.ReactNode;
  /** Ids and codes read better fixed-width and small. */
  mono?: boolean;
  /** Span both columns, for lists. */
  full?: boolean;
}) {
  return (
    <div className={full ? "col-span-2" : undefined}>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={mono ? "font-mono text-xs break-all" : undefined}>{children}</dd>
    </div>
  );
}

/** An id worth copying, or the same text plainly when there is nothing to copy. */
function Copyable({ value, children }: { value?: string | null; children: React.ReactNode }) {
  if (!value) {
    return <span className="font-sans text-muted-foreground">{children}</span>;
  }
  return (
    <span className="flex items-start gap-2">
      <span className="break-all">{children}</span>
      <CopyButton value={value} className="mt-0.5 shrink-0" />
    </span>
  );
}

/** The provider's account with its structure intact: which step reported it, and when. */
function FaultDetailList({ details }: { details?: FaultDetail[] }) {
  if (!details || details.length === 0) {
    return <span className="text-muted-foreground">none recorded</span>;
  }
  return (
    <ul className="space-y-2">
      {details.map((detail, i) => (
        <li key={i} className="rounded-md border bg-muted/30 p-2">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            {detail.step && <span className="text-xs font-medium">{detail.step}</span>}
            {detail.provider && (
              <span className="font-mono text-[11px] text-muted-foreground">{detail.provider}</span>
            )}
            {detail.code && (
              <Badge variant="outline" className="font-mono text-[10px]">
                {detail.code}
              </Badge>
            )}
            {detail.at && (
              <span className="ml-auto text-[11px] text-muted-foreground">
                {formatDateTime(detail.at)}
              </span>
            )}
          </div>
          {detail.message && (
            <p className="mt-1 text-xs text-muted-foreground">{detail.message}</p>
          )}
        </li>
      ))}
    </ul>
  );
}
